import { useEffect, useId, useRef, useState } from 'react'
import { formatDay, isAfterToday, tomorrow } from '../../account/dates'
import {
  actionsFor,
  chargeDescription,
  defaultResumeDay,
  scheduleLabel,
} from '../../account/subscription'
import type { Household } from '../../api/households'
import type { Subscription } from '../../api/subscriptions'
import { firstName, possessive, type User } from '../../api/users'
import { Choice } from '../ui/Choice'
import { TextField } from '../ui/TextField'
import { SavedIndicator, SettingEditButton, SettingForm, SettingRow } from './Setting'
import { useSubscriptionEditor } from './useSubscriptionEditor'

interface ScheduleSettingProps {
  subscription: Subscription
  child: User
  household: Household
  /** What the last change to the status did, to say beside the row it produced; see SubscriptionRows. */
  outcome: string | null
  setOutcome(outcome: string | null): void
}

/**
 * When the next charge comes, and what can be done about it: skip a kit,
 * resume a pause, restart after a cancellation. Nothing here predicts a
 * date; the row shows the one Recharge returns.
 *
 * A change to the status swaps one of these rows for another, so the row
 * that arrives says what happened and takes focus, since the control that
 * asked for the change went with the row it replaced.
 */
export function ScheduleSetting(props: ScheduleSettingProps) {
  switch (props.subscription.status) {
    case 'active':
      return <ActiveRow {...props} />
    case 'paused':
      return <PausedRow {...props} />
    case 'cancelled':
      return <CancelledRow {...props} />
  }
}

/**
 * Focus something once, when the row appears because of a change made from
 * the row it replaced. Asks `target` for the element only then, so an inline
 * arrow is fine.
 */
function useFocusOnOutcome(
  outcome: string | null,
  target: () => HTMLElement | null | undefined,
): void {
  const seen = useRef<string | null>(null)

  useEffect(() => {
    if (outcome === seen.current) return
    seen.current = outcome
    if (outcome) target()?.focus()
  }, [outcome, target])
}

/** The next charge, with a way to skip the kit it brings. */
function ActiveRow({ subscription, child, household, outcome, setOutcome }: ScheduleSettingProps) {
  const editor = useSubscriptionEditor({
    subscription,
    household,
    change: () => ({ skipNextCharge: true }),
    setOutcome,
  })
  const row = useRef<HTMLDivElement>(null)
  useFocusOnOutcome(outcome, () => editor.editButton.current ?? row.current)
  const due = subscription.nextChargeOn ? formatDay(subscription.nextChargeOn) : null
  const skip = actionsFor(subscription).skip && due !== null

  if (editor.open) {
    return (
      <SettingForm editor={editor} submitLabel="Skip this kit">
        <p className="text-base text-ink">
          Skip the kit due {due}? You won’t be charged for it, and the one after ships on its usual
          date.
        </p>
      </SettingForm>
    )
  }

  return (
    <SettingRow
      ref={row}
      label={scheduleLabel(subscription)}
      value={due ?? 'Not scheduled'}
      action={
        outcome || skip ? (
          <>
            {outcome && <SavedIndicator>{outcome}</SavedIndicator>}
            {skip && (
              <SettingEditButton
                editor={editor}
                onClick={editor.start}
                aria-label={`Skip ${possessive(firstName(child))} next kit`}
                savedLabel="Skipped"
              >
                Skip
              </SettingEditButton>
            )}
          </>
        ) : undefined
      }
      testId="setting-schedule"
    />
  )
}

/**
 * A pause, and its end. Resuming charges tomorrow — the earliest day
 * Recharge accepts — unless a later day is chosen, which is a new pause.
 */
function PausedRow({ subscription, child, household, outcome, setOutcome }: ScheduleSettingProps) {
  const [when, setWhen] = useState<'tomorrow' | 'later'>('tomorrow')
  const [resumeOn, setResumeOn] = useState('')
  const groupName = useId()
  const editor = useSubscriptionEditor({
    subscription,
    household,
    change: () => (when === 'tomorrow' ? { status: 'active' } : { status: 'paused', resumeOn }),
    validate: () =>
      when === 'later' && !isAfterToday(resumeOn) ? 'Choose a day after today.' : null,
    setOutcome,
  })
  const row = useRef<HTMLDivElement>(null)
  useFocusOnOutcome(outcome, () => editor.editButton.current ?? row.current)
  const until = subscription.nextChargeOn ? formatDay(subscription.nextChargeOn) : null
  const possessiveName = possessive(firstName(child))

  function start() {
    setWhen('tomorrow')
    setResumeOn(defaultResumeDay(subscription))
    editor.start()
  }

  if (editor.open) {
    return (
      <SettingForm editor={editor} submitLabel="Resume">
        <fieldset className="flex flex-col">
          <legend className="pb-1 text-base text-ink">Resume {possessiveName} subscription</legend>
          <Choice
            type="radio"
            name={groupName}
            value="tomorrow"
            checked={when === 'tomorrow'}
            onChange={() => setWhen('tomorrow')}
            description={`${chargeDescription(subscription)} tomorrow.`}
            data-testid="resume-tomorrow"
          >
            Tomorrow
          </Choice>
          <Choice
            type="radio"
            name={groupName}
            value="later"
            checked={when === 'later'}
            onChange={() => setWhen('later')}
            data-testid="resume-later"
          >
            On a later date
          </Choice>
        </fieldset>
        {when === 'later' && (
          <TextField
            type="date"
            label="Resume on"
            labelStyle="floating"
            min={tomorrow()}
            required
            invalid={editor.error !== null}
            value={resumeOn}
            onChange={(event) => setResumeOn(event.target.value)}
            data-testid="resume-on"
          />
        )}
      </SettingForm>
    )
  }

  return (
    <SettingRow
      ref={row}
      label="Subscription"
      value={until ? `Paused until ${until}` : 'Paused'}
      action={
        <>
          {outcome && <SavedIndicator>{outcome}</SavedIndicator>}
          <SettingEditButton
            editor={editor}
            onClick={start}
            aria-label={`Resume ${possessiveName} subscription`}
          >
            Resume
          </SettingEditButton>
        </>
      }
      testId="setting-schedule"
    />
  )
}

/** Cancelled, with a way back. Recharge picks the next date, so the row waits to show it. */
function CancelledRow({
  subscription,
  child,
  household,
  outcome,
  setOutcome,
}: ScheduleSettingProps) {
  const editor = useSubscriptionEditor({
    subscription,
    household,
    change: () => ({ status: 'active' }),
    setOutcome,
  })
  const row = useRef<HTMLDivElement>(null)
  useFocusOnOutcome(outcome, () => editor.editButton.current ?? row.current)
  const kit = subscription.tier === 'kit'
  const possessiveName = possessive(firstName(child))

  if (editor.open) {
    return (
      <SettingForm editor={editor} submitLabel="Restart">
        <p className="text-base text-ink">
          Restart {possessiveName} subscription? We’ll show the next {kit ? 'kit' : 'payment'} date
          as soon as it’s restarted.
        </p>
      </SettingForm>
    )
  }

  return (
    <SettingRow
      ref={row}
      label="Subscription"
      value="Cancelled"
      helper={kit ? 'No more kits or charges.' : 'No more charges.'}
      action={
        <>
          {outcome && <SavedIndicator>{outcome}</SavedIndicator>}
          <SettingEditButton
            editor={editor}
            onClick={editor.start}
            aria-label={`Restart ${possessiveName} subscription`}
          >
            Restart
          </SettingEditButton>
        </>
      }
      testId="setting-schedule"
    />
  )
}
